/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { Method, type MatrixClient } from "matrix-js-sdk";
import {
  type IWidgetApiRequestData,
  type IWidgetApiResponseData,
} from "matrix-widget-api";

import { ElementWidgetActions, widget, type WidgetHelpers } from "../widget";

export interface RoomInviteToken {
  id: number;
  value: string;
  created_by: string;
  created_at_ts: number;
  is_active: boolean;
}

export interface RoomInviteTokenResponse extends IWidgetApiResponseData {
  room_id: string;
  token: RoomInviteToken;
}

export interface RoomInviteTokenWidgetRequest extends IWidgetApiRequestData {
  operation: "get" | "rotate";
}

type InviteTokenWidget = Pick<WidgetHelpers, "api">;

const INVITE_TOKEN_PREFIX = "/_synapse/client/ru.avagroup.klava.room_shutdown";
const pendingLoads = new WeakMap<
  MatrixClient,
  Map<string, Promise<RoomInviteTokenResponse>>
>();

/** Ошибка запроса ссылки приглашения комнаты. */
export class RoomInviteLinkRequestError extends Error {
  public constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "RoomInviteLinkRequestError";
  }
}

function getInviteTokenPath(roomId: string): string {
  return `/rooms/${encodeURIComponent(roomId)}/token/my`;
}

/** Получает текущий токен приглашения комнаты. */
export async function getMyInviteToken(
  client: MatrixClient,
  roomId: string,
  embeddedWidget: InviteTokenWidget | null = widget,
): Promise<RoomInviteTokenResponse> {
  let clientLoads = pendingLoads.get(client);
  if (!clientLoads) {
    clientLoads = new Map();
    pendingLoads.set(client, clientLoads);
  }

  const existing = clientLoads.get(roomId);
  if (existing) return existing;

  const request = requestInviteToken(
    client,
    Method.Get,
    getInviteTokenPath(roomId),
    embeddedWidget,
  ).finally(() => clientLoads.delete(roomId));
  clientLoads.set(roomId, request);
  return request;
}

/** Обновляет токен приглашения комнаты. */
export async function rotateMyInviteToken(
  client: MatrixClient,
  roomId: string,
  embeddedWidget: InviteTokenWidget | null = widget,
): Promise<RoomInviteTokenResponse> {
  return requestInviteToken(
    client,
    Method.Post,
    `${getInviteTokenPath(roomId)}/rotate`,
    embeddedWidget,
  );
}

async function requestInviteToken(
  client: MatrixClient,
  method: Method,
  path: string,
  embeddedWidget: InviteTokenWidget | null,
): Promise<RoomInviteTokenResponse> {
  try {
    if (embeddedWidget) {
      // Полноценная Matrix-сессия принадлежит Element Web, поэтому widget
      // передаёт только разрешённую операцию, но не access token или URL.
      return await embeddedWidget.api.transport.send<
        RoomInviteTokenWidgetRequest,
        RoomInviteTokenResponse
      >(ElementWidgetActions.RoomInviteToken, {
        operation: method === Method.Get ? "get" : "rotate",
      });
    }

    return await client.http.authedRequest<RoomInviteTokenResponse>(
      method,
      path,
      {},
      undefined,
      { prefix: INVITE_TOKEN_PREFIX },
    );
  } catch (error) {
    const status = getHttpStatus(error);
    throw new RoomInviteLinkRequestError(
      status === undefined || status >= 500
        ? "Сервис приглашений временно недоступен. Попробуйте снова."
        : "Не удалось получить ссылку приглашения.",
      status,
    );
  }
}

function getHttpStatus(error: unknown): number | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "httpStatus" in error &&
    typeof error.httpStatus === "number"
  ) {
    return error.httpStatus;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null &&
    "matrix_api_error" in error.data &&
    typeof error.data.matrix_api_error === "object" &&
    error.data.matrix_api_error !== null &&
    "http_status" in error.data.matrix_api_error &&
    typeof error.data.matrix_api_error.http_status === "number"
  ) {
    return error.data.matrix_api_error.http_status;
  }
  return undefined;
}

/** Формирует пользовательскую invite-ссылку из базового URL и токена. */
export function buildInviteLinkUrl(baseUrl: string, token: string): string {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
  try {
    return new URL(
      `/invite/${encodeURIComponent(token)}`,
      normalizedBaseUrl,
    ).toString();
  } catch {
    return `${normalizedBaseUrl}/invite/${encodeURIComponent(token)}`;
  }
}
